import { useState, useRef, useEffect } from "react";
import "./styles/_index.scss";
import store from "../../store";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { Card, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import CardPlan from "../../components/dashboard/card";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import axios from "axios";

const drawerWidth = 0;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

interface PdfStatus {
  Typhoon: boolean;
  [key: string]: boolean;
}

export default function DashBoard() {
  // Reference value for current Browser Window Width
  const windowWidth = useRef(window.innerWidth);
  const [stormData, setStormData] = useState([]);
  const [mapHovers, setMapHovers] = useState<any>([]);
  const [loadedDatas, setLoadedDatas] = useState(false);
  // Sets Track about the SideNav Bar Open/Close State
  const [open, setOpen] = useState(windowWidth.current > 1000 ? true : false);
  const data = useSelector((state: any) => state?.app);
  //const data = useSelector((state: any) => state?.app);
  const isAdmin = localStorage.getItem("type") === "admin";
  const [operationOpen, setOperationOpen] = useState(false);

  const [discussion, setDiscussion] = useState<any>("");
  const [fid, setFid] = useState<string>("");
  const [noDataMessage, setNoDataMessage] = useState("");
  const [forecastFetchDone, setForecastFetchDone] = useState(false);

  const [pdfStatus, setPdfStatus] = useState<PdfStatus>({ Typhoon: false });
  const checkPdfAvailability = async () => {
    try {
      const project = localStorage.getItem("project");
      const location =
        localStorage.getItem("project_location") ||
        localStorage.getItem("location") ||
        localStorage.getItem("project") ||
        "";

      if (!project || !location) {
        //console.log("No project or location found");
        setPdfStatus((prevStatus) => ({
          ...prevStatus,
          Typhoon: false,
        }));
        return;
      }

      if (!stormData || stormData.length === 0) {
        setPdfStatus((prevStatus) => ({ ...prevStatus, Typhoon: false }));
        return;
      }

      const activeStorms = stormData.filter(
        (data: any) => data.status_id === 8,
      );
      if (activeStorms.length === 0) {
        setPdfStatus((prevStatus) => ({ ...prevStatus, Typhoon: false }));
        return;
      }

      const stormNames = activeStorms.map((storm: any) => storm.storm_name);

      const axiosConfig = {
        method: "post",
        url: `${process.env.REACT_APP_BACKEND_IP}/api/checkpdftyphoon/`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          location: location,
          storm_name: stormNames,
        },
        timeout: 3000,
      };

      const response = await axios(axiosConfig);

      if (response.status === 200 && response.data) {
        const { count, files } = response.data;

        const parsedFiles = files
          ? files.map((fileItem: any) => {
              const filename =
                typeof fileItem === "string" ? fileItem : fileItem.filename;
              const match = filename.match(
                /TC_BULLETIN_([^-]+)-([^-]+)-([^-]+)/,
              );
              if (match) {
                const [, stormName, fileLocation, projectName] = match;
                return {
                  filename: filename,
                  storm_name: stormName.trim(),
                  location: fileLocation.trim(),
                  project: projectName.trim(),
                };
              }

              const extractStormName = (filename: string): string => {
                if (filename.includes("-")) {
                  const match = filename.match(/TC_BULLETIN_([^-]+)/);
                  return match ? match[1] : filename;
                } else {
                  const match = filename.match(/TC_BULLETIN_(.+)/);
                  return match ? match[1] : filename;
                }
              };

              return {
                filename: filename,
                storm_name: extractStormName(filename),
                location: filename,
                project: project,
              };
            })
          : [];

        const locationMatchingFiles = parsedFiles.filter((fileData: any) => {
          return (
            fileData &&
            (fileData.location.toUpperCase().includes(location.toUpperCase()) ||
              location.toUpperCase().includes(fileData.location.toUpperCase()))
          );
        });

        const locationSpecificCount = locationMatchingFiles.length;
        const hasLocationSpecificPdf = locationSpecificCount > 0;

        setPdfStatus((prevStatus) => ({
          ...prevStatus,
          Typhoon: hasLocationSpecificPdf,
        }));
      } else {
        setPdfStatus((prevStatus) => ({
          ...prevStatus,
          Typhoon: false,
        }));
      }
    } catch (error) {
      // console.error("Error checking PDF availability:", error);
      setPdfStatus((prevStatus) => ({
        ...prevStatus,
        Typhoon: false,
      }));
    }
  };

  useEffect(() => {
    if (stormData && stormData.length > 0) {
      checkPdfAvailability();
      const intervalId = setInterval(checkPdfAvailability, 5000);
      return () => clearInterval(intervalId);
    }
  }, [stormData]);

  useEffect(() => {
    store.dispatch({
      type: "TOGGLE_MENU",
      payload: windowWidth.current > 1000 ? true : false,
    });
  }, []);

  useEffect(() => {
    setOpen(data.toggle);
  }, [data]);

  useEffect(() => {
    if (loadedDatas) {
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_IP}/api/stormdatas/`)
      .then((data) => data.json())
      .then((data) => {
        setStormData(data.track_datas);
        setMapHovers(data.map_hovers);
        setLoadedDatas(true);
      })
      .catch((error) => setLoadedDatas(true));
  });

  const [gif, setGif] = useState(false);
  const [file, setFile] = useState(false);
  const [jsonData, setJsonData] = useState<any>();
  const [error, setError] = useState();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [SquallMap, setSquallMap] = useState("");
  const [userLat, setUserLat] = useState(0);
  const [userLon, setUserLon] = useState(0);
  const [PrevLat, setPrevLat] = useState(0);
  const [PrevLon, setPrevLon] = useState(0);
  const [CurLat, setCurLat] = useState(0);
  const [CurLon, setCurLon] = useState(0);
  const [init, setInit] = useState<any>();
  const [storm, setStorm] = useState<any>();
  const [intensity, setIntensity] = useState<any>();
  const [fc, setFc] = useState<any>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_IP}/api/getcsv/${localStorage.fid}`,
        );
        if (response.status === 404) {
          setTitle(
            "There are currently no severe warnings active for this location",
          );
        } else {
          const csvText = response.data;
          setJsonData(csvText);
          setTitle(csvText[0]["Squall Header"]);
          setDesc(csvText[0]["Squall Advisory"]);
          setSquallMap(csvText[0]["SatImgSection"]);
          setUserLat(csvText[0]["Client's lat"]);
          setUserLon(csvText[0]["Client's lon"]);
          setCurLat(csvText[0]["Previous Sat lat"]);
          setCurLon(csvText[0]["Previous Sat lon"]);
          setPrevLat(csvText[0]["Latest Sat lat"]);
          setPrevLon(csvText[0]["Latest Sat lon"]);
          setInit(csvText[0]["Initial Heading"]);
          setStorm(csvText[0]["Storm Speed"]);
          setIntensity(csvText[0]["Squall Intensity"]);
          setFc(csvText[0]["Forecaster"]);
        }
      } catch (error: any) {
        setError(error.message || "An error occurred while fetching data");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
  const resolvedFid = localStorage.getItem("fid") ?? "";
  setFid(resolvedFid);
}, []);

   useEffect(() => {
    if (!fid) return;
    const fetchData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_IP}/api/forecast/`,
          { forecast_id: fid, length: 0 },
          { headers: { "Content-type": "application/json; charset=UTF-8" } },
        );
        const resData = response.data;     
        setDiscussion(resData.discussion.discussion);
      } catch (err: any) {
        setNoDataMessage("Error fetching forecast data");
      } finally {
        setForecastFetchDone(true);
      }
    };
    fetchData();
  }, [fid]);

  return (
    <div className={open ? "sideNavOpen" : "sideNavClose"}>
      <Box className="fug-container bg-default flex sidenav-full">
        <div className="content-wrap dashboard">
          <Main open={open} className={"main"}>
            <Grid container spacing={2} display={"flex"} alignItems={"center"}>
              <Grid xs={12} md={9}>
                <Grid container spacing={3}>
                  <CardPlan />
                </Grid>
              </Grid>
              <Grid xs={12} md={3} marginTop={"-10px"} height={"63vh"}>
                <Card
                  style={{
                    borderRadius: "15px",
                    marginBottom: "1em",
                    backgroundColor: "#003366",
                    color: "white",
                  }}
                >
                  <Grid xs={12} sx={{ height: 100 }}>
                    <Typography
                      variant="body2"
                      color="white"
                      align="center"
                      fontWeight="bold"
                    >
                      Squall Warnings
                    </Typography>
                    {!jsonData || jsonData.length === 0 ? (
                      <Typography variant="body2" color="green" align="center">
                        There are currently no severe warnings active for this
                        location
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="red"
                        align="center"
                        fontWeight="light"
                      >
                        Squall is Active
                      </Typography>
                    )}
                  </Grid>
                </Card>
                <Card
                  style={{
                    borderRadius: "15px",
                    marginBottom: "1em",
                    backgroundColor: "#003366",
                    color: "white",
                  }}
                >
                  <Grid xs={12} sx={{ height: 130 }}>
                    <Typography
                      variant="body2"
                      color="white"
                      style={{ textAlign: "center" }}
                      fontWeight="bold"
                    >
                      Tropical Cyclone Warnings
                    </Typography>
                    {pdfStatus.Typhoon &&
                    stormData &&
                    stormData.some((data: any) => data.status_id === 8) ? (
                      stormData
                        .filter((data: any) => data.status_id === 8)
                        .map((data: any) => (
                          <Typography
                            key={data.storm_track_id}
                            className="blink-text-red"
                            style={{
                              textAlign: "center",
                              paddingBlock: "5px",
                              color: "red",
                            }}
                          >
                            {data.storm_name} -{" "}
                            {data.created_on
                              ?.split("-")
                              ?.slice(1, 3)
                              ?.join("/")}
                          </Typography>
                        ))
                    ) : (
                      <Typography
                        variant="body2"
                        style={{
                          textAlign: "center",
                          paddingBlock: "5px",
                          color: "green",
                        }}
                      >
                        No Typhoon Warnings
                      </Typography>
                    )}
                  </Grid>
                </Card>
                <Card
                  style={{
                    borderRadius: "15px",
                    backgroundColor: "#003366",
                    color: "white",
                  }}
                >
                  <Grid xs={12} sx={{ height: 100 }}>
                    <Typography variant="body2" color="white" align="center">
                      <b>Tropical Advisory</b>
                    </Typography>
                    <Typography variant="body2" color="green" align="center">
                    {discussion?.advisory?.trim() || "No Tropical Advisory"}
                   </Typography>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Main>
        </div>
      </Box>
    </div>
  );
}
